import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { startFancyTextCopy } from '@/pages/krasivyy-tekst/fancy-text-analytics'
import {
  buildFancyTextResults,
  FANCY_TEXT_STYLE_IDS,
  MAX_FANCY_TEXT_CODE_POINTS,
  MAX_FANCY_TEXT_GRAPHEMES,
  validateFancyText,
  type FancyTextStyle,
} from '@/pages/krasivyy-tekst/fancy-text'

type LimitError = 'grapheme_limit' | 'code_point_limit'

export function FancyTextPage() {
  return (
    <ToolPageShell toolKey="fancyText">
      <FancyTextWorkspace />
    </ToolPageShell>
  )
}

export function FancyTextWorkspace() {
  const t = useT()
  const { locale } = useLocale()
  const p = t.fancyText
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [limitError, setLimitError] = useState<LimitError | null>(null)
  const [liveMessage, setLiveMessage] = useState('')
  const revisionRef = useRef(0)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const validation = useMemo(() => validateFancyText(input), [input])
  const results = useMemo(() => buildFancyTextResults(input), [input])
  const hasCopyableInput = input.trim().length > 0

  const handleInput = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value
    const nextValidation = validateFancyText(next)
    if (nextValidation.status === 'error') {
      setLimitError(nextValidation.error)
      setLiveMessage(
        nextValidation.error === 'grapheme_limit'
          ? p.graphemeLimitError
          : p.codePointLimitError,
      )
      return
    }

    revisionRef.current += 1
    setInput(next)
    setLimitError(null)
    setLiveMessage('')
  }, [p.codePointLimitError, p.graphemeLimitError])

  const handleClear = useCallback(() => {
    revisionRef.current += 1
    setInput('')
    setLimitError(null)
    setLiveMessage('')
    inputRef.current?.focus()
  }, [])

  const copy = useCallback(async (value: string, styleName: string) => {
    if (!hasCopyableInput) return
    const revision = revisionRef.current
    const startedAt = performance.now()
    const operation = startFancyTextCopy(value === input ? 'unchanged' : 'changed')

    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable')
      await navigator.clipboard.writeText(value)
      if (revision !== revisionRef.current) return
      operation.success(performance.now() - startedAt)
      setLiveMessage(p.copySuccess.replace('{style}', styleName))
    } catch {
      if (revision !== revisionRef.current) return
      operation.error('processing_failed')
      setLiveMessage(p.copyError.replace('{style}', styleName))
    }
  }, [hasCopyableInput, input, p.copyError, p.copySuccess])

  const styleNames = p.styles satisfies Record<FancyTextStyle, string>
  const counts = validation.counts

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
          <div>
            <label htmlFor="fancy-text-input" className="text-sm font-semibold">
              {p.inputLabel}
            </label>
            <p id="fancy-text-instructions" className="mt-1 hidden text-xs leading-5 text-muted-foreground sm:block">
              {p.instructions}
            </p>
          </div>
        </div>
        <textarea
          ref={inputRef}
          id="fancy-text-input"
          value={input}
          onChange={handleInput}
          placeholder={p.placeholder}
          aria-describedby={cn(
            'fancy-text-instructions fancy-text-privacy',
            input && 'fancy-text-counts',
            limitError && 'fancy-text-limit-error',
          )}
          className={cn(
            'ym-disable-keys ym-disable-submit block min-h-28 w-full resize-y border-0 bg-card p-4 sm:min-h-40 sm:p-5',
            'text-[15px] leading-7 placeholder:text-muted-foreground/55',
            'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
          )}
        />
        {input && (
          <>
            <div id="fancy-text-counts" className="grid gap-2 border-t bg-muted/20 px-4 py-3 text-xs text-muted-foreground sm:grid-cols-3">
              <span>{p.graphemes}: {counts.graphemes.toLocaleString(localeCode)} / {MAX_FANCY_TEXT_GRAPHEMES.toLocaleString(localeCode)}</span>
              <span>{p.codePoints}: {counts.codePoints.toLocaleString(localeCode)} / {MAX_FANCY_TEXT_CODE_POINTS.toLocaleString(localeCode)}</span>
              <span>{p.utf16Units}: {counts.utf16Units.toLocaleString(localeCode)} — {p.checkAfterPaste}</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t px-4 py-3">
              <Button variant="outline" size="sm" onClick={handleClear}>
                {p.clear}
              </Button>
              {hasCopyableInput && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(input, p.originalStyleName)}
                >
                  {p.copyOriginal}
                </Button>
              )}
            </div>
          </>
        )}
      </Card>

      {limitError && (
        <div id="fancy-text-limit-error" role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {limitError === 'grapheme_limit' ? p.graphemeLimitError : p.codePointLimitError}
        </div>
      )}

      {validation.status === 'success' && validation.hasUnsafeControls && (
        <div role="alert" className="rounded-lg border border-amber-400/60 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100">
          {p.safetyWarning}
        </div>
      )}

      <section aria-labelledby="fancy-text-results-title">
        <h2 id="fancy-text-results-title" className="text-xl font-semibold">{p.resultsTitle}</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {FANCY_TEXT_STYLE_IDS.map((style) => {
            const styleName = styleNames[style]
            const output = results[style]
            return (
              <Card key={style} className="min-w-0 overflow-hidden shadow-sm" role="group" aria-labelledby={`fancy-text-${style}`}>
                <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/35 px-4 py-2">
                  <h3 id={`fancy-text-${style}`} className="text-sm font-semibold">{styleName}</h3>
                  {hasCopyableInput && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copy(output, styleName)}
                      aria-label={p.copyStyle.replace('{style}', styleName)}
                    >
                      {p.copy}
                    </Button>
                  )}
                </div>
                <div
                  aria-hidden="true"
                  className="ym-hide-content min-h-16 overflow-auto whitespace-pre-wrap break-words p-4 text-lg leading-8"
                >
                  {output || <span className="text-sm text-muted-foreground/60">{p.emptyResult}</span>}
                </div>
                {input && (
                  <p className="border-t px-4 py-2 text-xs leading-5 text-muted-foreground">
                    {output === input ? p.unchangedResult : p.partialResult}
                  </p>
                )}
              </Card>
            )
          })}
        </div>
      </section>

      <Card className="border-violet-300/70 bg-violet-50/55 shadow-sm dark:border-violet-800 dark:bg-violet-950/25">
        <CardContent className="p-4 text-sm leading-6">
          <p className="font-medium">{p.coverageTitle}</p>
          <p className="mt-1 text-muted-foreground">{p.coverageText}</p>
        </CardContent>
      </Card>

      <Card className="border-dashed bg-card/70 shadow-sm">
        <CardContent className="space-y-2 p-4 text-sm leading-6 text-muted-foreground">
          <p id="fancy-text-privacy" className="font-medium text-foreground">{p.privacy}</p>
          <p>{p.compatibility}</p>
          <p>{p.accessibility}</p>
        </CardContent>
      </Card>

      <p role="status" aria-live="polite" aria-atomic="true" className="min-h-6 text-sm text-muted-foreground">
        {liveMessage}
      </p>
    </div>
  )
}
